
import { computed, defineComponent, onMounted, onUnmounted, ref } from "vue";
import { useChannelSelector } from "@/composables/channelSelector";
import { useMessengerHeader } from "@/composables/messengerHeader";
import { ConversationChannel } from "@/types/channels";
import { FormKitGroupValue, FormKitSchemaNode } from "@formkit/core";
import { useChatConnection } from "@/composables/chatConnection";
import { useWidgetConfig } from "@/composables/widgetConfig";
import { WidgetConfigurationPreChatSurveyField } from "@/types/inboundWebhookPayload";
import Button from "@/components/Button.vue";
import { useI18n } from "vue-i18n";

const fieldTypeToFormkitTypeMap: Record<
  WidgetConfigurationPreChatSurveyField["type"],
  string
> = {
  name: "text",
  email: "email",
  "phone-number": "text",
};

const fieldTypeValidationMap: Record<
  WidgetConfigurationPreChatSurveyField["type"],
  string
> = {
  name: "alpha_spaces:default",
  email: "email",
  "phone-number": "phone",
};

export default defineComponent({
  name: "MessengerStartChatForm",
  components: {
    Button,
  },
  setup(props) {
    const { selectedChannel, setChannel } = useChannelSelector();
    const { setBackAction } = useMessengerHeader();
    const { requestStartConversation } = useChatConnection();
    const { canStartDirectAnyChannel, configuration } = useWidgetConfig();
    const loading = ref(false);
    const { t } = useI18n();

    const schema = computed(() => {
      const res: Array<FormKitSchemaNode> = [];

      if (!configuration.value?.preChatSurveyFields) {
        return res;
      }

      configuration.value?.preChatSurveyFields.forEach((field) => {
        const obj: FormKitSchemaNode = {
          name: field.id,
          $formkit: fieldTypeToFormkitTypeMap[field.type],
          validation: field.required
            ? `required|${fieldTypeValidationMap[field.type]}`
            : fieldTypeValidationMap[field.type],
          label: field.title,
          help: field.hint,
        };

        if (field.placeholder) {
          obj.attrs.placeholder = field.placeholder;
        }

        res.push(obj);
      });

      return res;
    });

    const submit = (form: FormKitGroupValue) => {
      requestStartConversation(form);
      return new Promise((resolve) => setTimeout(resolve, 10000));
    };

    if (selectedChannel.value !== ConversationChannel.Website) {
      throw new Error(
        "Cannot use chat form for other channels than Website, was " +
          selectedChannel.value
      );
    }

    onMounted(() => {
      if (canStartDirectAnyChannel.value) {
        setBackAction(() => setChannel(undefined));
      }
    });

    onUnmounted(() => {
      setBackAction(null);
    });

    return {
      submit,
      schema,
      t,
    };
  },
});
